import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogPortal,
    DialogOverlay,
} from '../atoms/dialog';
import { UserLandsStep, type UserLandsStepData } from './UserLandsStep';
import stepsData from './userLandsStepsExample.json';

export interface UserLandsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    isWeb: boolean;
    steps?: UserLandsStepData[];
    onTryPrototyper?: () => void;
    onViewAllUpdates?: () => void;
}

export const UserLandsDialog: React.FC<UserLandsDialogProps> = ({
    isOpen,
    onClose,
    isWeb,
    steps: externalSteps,
    onTryPrototyper,
    onViewAllUpdates,
}) => {
    const [currentStep, setCurrentStep] = useState(1);

    const handleNext = () => {
        if (currentStep < (externalSteps?.length || defaultSteps.length)) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleTryPrototyper = () => {
        if (onTryPrototyper) {
            onTryPrototyper();
        }
        onClose();
    };

    const handleViewAllUpdates = () => {
        if (onViewAllUpdates) {
            onViewAllUpdates();
        } else {
            console.log('View all updates clicked');
        }
    };

    // Load steps from JSON and add action handlers
    const defaultSteps: UserLandsStepData[] = useMemo(
        () =>
            stepsData.map((step, index) => ({
                stepNumber: step.stepNumber,
                videoLink: step.videoLink,
                badge: step.badge
                    ? {
                        text: step.badge.text,
                        icon: step.badge.icon as 'rocket',
                    }
                    : undefined,
                title: step.title,
                description: step.description,
                actions: {
                    left: step.actions.left
                        ? {
                            text: step.actions.left.text,
                            variant: step.actions.left.variant as 'outline' | 'primary',
                            action: index === stepsData.length - 1 ? handleViewAllUpdates : handleBack,
                        }
                        : undefined,
                    right: step.actions.right
                        ? {
                            text: step.actions.right.text,
                            variant: step.actions.right.variant as 'outline' | 'primary',
                            action: index === stepsData.length - 1 ? handleTryPrototyper : handleNext,
                        }
                        : undefined,
                },
            })),
        [handleBack, handleNext, handleTryPrototyper, handleViewAllUpdates]
    );

    const steps = externalSteps || defaultSteps;
    const currentStepData = steps[currentStep - 1];
    const isLastStep = currentStep === steps.length;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="max-w-[480px] w-full p-0 overflow-hidden rounded-[24px] sm:rounded-[24px]">
                    {currentStepData && (
                        <UserLandsStep
                            data={currentStepData}
                            isLastStep={isLastStep}
                            currentStep={currentStep}
                            totalSteps={steps.length}
                            isWeb={isWeb}
                        />
                    )}
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
};
